// Copyright (c) 2022 by Move Closer

import { FooterProps } from '../../front/shared/organisms/Footer/Footer.contracts'
import { ImageData, IRelatedService, ModuleContentResolver, NavigationItem } from '../../contexts'
import { logger } from '../../support'
import { toImageProps } from '../../front/shared/support'

import {
  FooterModuleContent,
  FooterModuleContentInput, FooterResolvedBadge
} from './Footer.contracts'
import { isUnresolvedLink, ResolvedLink } from '../partials'
import { resolveImage, resolveLink, resolveNavigation } from '../resolvers'

/**
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl>
 */
export const DEFAULT_SITE_AUTHOR = {
  label: 'movecloser.co',
  target: 'https://movecloser.co'
}

/**
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl>
 * @author Filip Rurak <filip.rurak@movecloser.pl>
 */
export const translateToFooterProps = (input: FooterModuleContent): FooterProps => ({
  badge: typeof input.badge !== 'undefined' ? {
    amount: input.badge?.amount ? input.badge.amount : null,
    desc: input.badge?.desc ? input.badge.desc : null,
    icon: input.badge?.icon ? input.badge.icon : null,
    rate: input.badge?.rate ? input.badge.rate : null,
    sectionTitle: input.badge?.sectionTitle ? input.badge.sectionTitle : null,
    link: input.badge?.link ? input.badge.link : undefined
  } : undefined,

  badgeType: typeof input.badgeType !== 'undefined' ? input.badgeType : undefined,

  contactNavigationLinks: typeof input.contactNav !== 'undefined' ? input.contactNav : undefined,

  descriptionSection: typeof input.description !== 'undefined' ? input.description : undefined,

  deliveryMethodsNavigationLinks: typeof input.deliveryNav !== 'undefined' ? input.deliveryNav : undefined,

  paymentMethodsNavigationLinks: typeof input.paymentNav !== 'undefined' ? input.paymentNav : undefined,

  mainNavigationLinks: typeof input.mainNav !== 'undefined' ? input.mainNav : undefined,

  secondaryNavigationLinks: typeof input.secondaryNav !== 'undefined' ? input.secondaryNav : undefined,

  siteAuthor: typeof input.authorSiteLink !== 'undefined' ? {
    target: input.authorSiteLink.target,
    label: input.authorSiteLink.label
  } : DEFAULT_SITE_AUTHOR,

  siteLogo: typeof input.brandImage !== 'undefined' ? {
    ...toImageProps(input.brandImage),
    width: input.brandImageDimensions?.maxWidth,
    height: input.brandImageDimensions?.maxHeight
  } : undefined,

  socialLinks: typeof input.socialNav !== 'undefined' ? input.socialNav : undefined
})

/**
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl>
 * @author Filip Rurak <filip.rurak@movecloser.pl> (edited)
 */
export const resolveFooterModuleContent: ModuleContentResolver<FooterModuleContentInput, FooterModuleContent> =
  async (input: FooterModuleContentInput, relatedService: IRelatedService): Promise<FooterModuleContent> => {
    let authorSiteLink: ResolvedLink | undefined
    let badge: FooterResolvedBadge | undefined
    let brandImage: ImageData | undefined
    let contactNav: NavigationItem<true>[] | undefined
    let deliveryNav: NavigationItem<true>[] | undefined
    let description: string | undefined
    let mainNav: NavigationItem<true>[] | undefined
    let logoLink: ResolvedLink | null | undefined
    let paymentNav: NavigationItem<true>[] | undefined
    let secondaryNav: NavigationItem<true>[] | undefined
    let socialNav: NavigationItem<true>[] | undefined

    if (typeof input.authorSiteLink !== 'undefined' && input.authorSiteLink) {
      if (!isUnresolvedLink(input.authorSiteLink)) {
        authorSiteLink = {
          label: typeof input.authorSiteLink.label === 'undefined' ? '' : input.authorSiteLink.label.toString(),
          target: input.authorSiteLink.target
        }
      } else {
        try {
          authorSiteLink = await resolveLink(input.authorSiteLink, relatedService)
        } catch (e) {
          logger(e, 'warn')
        }
      }
    }

    if (typeof input.badge !== 'undefined' && input.badge) {
      let badgeLink

      if (typeof input.badge.link !== 'undefined' && input.badge.link) {
        if (!isUnresolvedLink(input.badge.link)) {
          badgeLink = input.badge.link
        } else {
          try {
            badgeLink = await resolveLink(input.badge.link, relatedService)
          } catch (e) {
            logger(e, 'warn')
          }
        }
      } else {
        badgeLink = null
      }

      badge = {
        amount: input.badge.amount,
        desc: String(input.badge.desc),
        icon: String(input.badge.icon),
        rate: input.badge.rate,
        sectionTitle: String(input.badge.sectionTitle),
        link: badgeLink
      }
    }

    if (typeof input.brandImage !== 'undefined' && input.brandImage) {
      try {
        brandImage = await resolveImage(input.brandImage, relatedService)
      } catch (e) {
        logger(e, 'warn')
      }
    }

    if (typeof input.contactNav !== 'undefined' && input.contactNav) {
      try {
        contactNav = await resolveNavigation(input.contactNav, relatedService)
      } catch (e) {
        logger(e, 'warn')
      }
    }

    if (typeof input.deliveryNav !== 'undefined' && input.deliveryNav) {
      try {
        deliveryNav = await resolveNavigation(input.deliveryNav, relatedService)
      } catch (e) {
        logger(e, 'warn')
      }
    }

    if (typeof input.description !== 'undefined') {
      description = input.description
    }

    if (typeof input.mainNav !== 'undefined' && input.mainNav) {
      try {
        mainNav = await resolveNavigation(input.mainNav, relatedService)
      } catch (e) {
        logger(e, 'warn')
      }
    }

    if (typeof input.paymentNav !== 'undefined' && input.paymentNav) {
      try {
        paymentNav = await resolveNavigation(input.paymentNav, relatedService)
      } catch (e) {
        logger(e, 'warn')
      }
    }

    if (typeof input.secondaryNav !== 'undefined' && input.secondaryNav) {
      try {
        secondaryNav = await resolveNavigation(input.secondaryNav, relatedService)
      } catch (e) {
        logger(e, 'warn')
      }
    }

    if (typeof input.socialNav !== 'undefined' && input.socialNav) {
      try {
        socialNav = await resolveNavigation(input.socialNav, relatedService)
      } catch (e) {
        logger(e, 'warn')
      }
    }

    if (typeof input.logoLink !== 'undefined' && input.logoLink) {
      try {
        logoLink = await resolveLink(input.logoLink, relatedService)
      } catch (e) {
        logger(e, 'warn')
      }
    }

    return {
      ...input,
      authorSiteLink,
      badge,
      brandImage,
      contactNav,
      deliveryNav,
      description,
      mainNav,
      logoLink,
      paymentNav,
      secondaryNav,
      socialNav
    }
  }
